// Help Center - LPS Solutions platform guide.
import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Card, CardContent,
  Grid, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const TOPICS = [
  {
    icon: '🎙️', title: 'Getting started',
    items: [
      ['Upload your first recording', 'Recordings → Upload recording. Pick an audio file (wav/mp3/m4a), set a title, department and optional subject ID. Processing runs entirely inside your environment.'],
      ['What happens during processing', 'Transcription → speaker separation → conversation metrics → a structured, editable report. Status is live in the Recordings list.'],
      ['Supported recordings', 'Up to 8 hours per file. Meeting-platform connectors (Zoom, Teams) arrive with the connected deployment; air-gapped deployments use upload and bulk import.'],
    ],
  },
  {
    icon: '📄', title: 'Reports',
    items: [
      ['Editing a report', 'Every section is editable text - the report is a document, not a locked PDF. Save drafts as you go; Finalize & PDF renders the printable version.'],
      ['Report sections', 'Summary, key points, action items and notable quotes by default; your industry pack adds sections like Commitments & Positions (legal) or Risks (HLS).'],
      ['Conversation metrics', 'Fact-based only: duration, words, talk-time per speaker, turns, silences. Emotional analysis is opt-in per user and OFF by default.'],
    ],
  },
  {
    icon: '💬', title: 'Ask Your Records',
    items: [
      ['Asking questions', 'Ask in natural language across one recording or the whole archive. Answers cite the exact transcript segments (speaker + timestamp) they rely on.'],
      ['Scope control', 'Use the scope selector to limit a question to a single recording - useful for long sessions.'],
    ],
  },
  {
    icon: '🔐', title: 'Roles & security',
    items: [
      ['Roles', 'Org Admin (everything), Dept Manager, Contributor (upload + edit), Analyst (read + chat + export), Auditor (org-wide read-only + audit), Viewer (explicitly shared items only).'],
      ['Subject identifiers', 'Patient/subject IDs are visible only to roles with identifier permission; other roles see them masked.'],
      ['Audit trail', 'Every action is recorded in Security & Audit - ids and counts only, never content. LPS has no access to your deployment unless you explicitly grant the time-boxed support role.'],
    ],
  },
  {
    icon: '☁️', title: 'Deployment',
    items: [
      ['Where does my data live?', 'Everything - audio, transcripts, reports, the AI models themselves - runs inside your AWS account (or an LPS-hosted dedicated tenant). No internet AI APIs, no token costs.'],
      ['Air-gap mode', 'Deployments can run with no internet connectivity at all: models ship pre-installed, and all AWS access flows through private VPC endpoints.'],
      ['Industry packs', 'Org Admins switch industry configuration (hospital / clinic / legal / HR / HLS) under Organization - it drives report templates, terminology and dashboards.'],
    ],
  },
];

const FAQS = [
  ['Does audio ever leave our environment?', 'No. Processing, storage, search and chat all run inside your deployment. The air-gap mode makes this physically enforceable - there is no internet path.'],
  ['Which languages are supported?', 'Hebrew and English first, with additional languages per the deployed model pack.'],
  ['Can we connect our identity provider?', 'Yes - SAML/OIDC (Okta, Entra ID, ADFS) and Active Directory, plus local accounts with MFA as break-glass.'],
  ['How is this priced?', 'Annual license per deployment. Connected deployments can meter processed audio hours; air-gapped deployments are license-only - compute is yours.'],
  ['Who can delete recordings?', 'Org Admins only, and deletion is soft with a retention hold - satisfying both privacy law and audit requirements.'],
];

export default function LpsHelpPage() {
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>Help Center</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Private meeting intelligence, on your infrastructure. Questions? info@lps.solutions
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {TOPICS.map((t) => (
          <Grid item xs={12} md={6} key={t.title}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>{t.icon} {t.title}</Typography>
                {t.items.map(([h, b]) => (
                  <Box key={h} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>{h}</Typography>
                    <Typography variant="body2" color="text.secondary">{b}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" sx={{ mb: 1 }}>FAQs</Typography>
      {FAQS.map(([q, a]) => (
        <Accordion key={q} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>{q}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">{a}</Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
